import { useRef, useState } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { v4 as uuidv4 } from 'uuid';
import '@/shared/quiz/events.quiz-build';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';
import type { CreateQuizSetReq } from '@/api/gen/_model/createQuizSetReq';
import type { UpdateQuizSetReq } from '@/api/gen/_model/updateQuizSetReq';
import { getGetQuizSetBuildInfoQueryKey, useCreateQuizSet, useEditQuizSet } from '@/api/gen/quiz/quiz.gen';
import { getGetQuizzesDetailStatsQueryKey, getGetQuizzesStatsQueryKey } from '@/api/gen/quizset-stats/quizset-stats.gen';
import { ZepQuizApiError } from '@/api/httpClient';
import { MY_QUIZ_LIST_QUERY_KEY, QUIZ_DETAIL_QUERY_KEY } from '@/api/quiz';
import { useAlert } from '@/hooks/useAlert';
import { CheckSmallIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { libraryKeys } from '@/shared/features/quiz-library/api/library.keys';
import { myQuizListKey } from '@/shared/features/quiz-list/hooks/useFetchMyQuizes';
import { useCreateAssignmentDialogEffect, useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { ltiContextManager } from '@/shared/lti';
import { useUserPlanManager } from '@/shared/plan';
import { QUIZ_TYPE_PLAN_FEATURE } from '@/shared/plan/consts';
import { type PlanType } from '@/types';
import { alertDialog, Button } from '@/ui_v3';
import { createElapsedTimeLogger } from '@/utils/createElapsedTimeLogger';
import { useBuildEventTrack } from '@/views/build/action/useBuildEventTrack';
import { useBuildValidate } from '@/views/build/action/useBuildValidate';
import { focusField } from '@/views/build/components/focus/focusUtils';
import { useBuildInfoContext } from '@/views/build/context';
import { getBuildEntryPoint } from '@/views/build/features/enter-tracking/utils/buildEntryPointStore';
import { type BuildSchemaInputType } from '@/views/build/form/schema';
import { usePersistBuildForm } from '@/views/build/hook';
import { DisabledEditAlertModal } from '@/views/build/modal';
import { useQuizTitle } from '../features/build-quiz-title';
import { useBuildFormContext } from '../form/schema';
import { transformBuildFormToApiRequest } from '../util/buildFormToApiTransformer';
import { getBuildType } from '../util/getBuildType';
import { resolveMapTemplateAvailability } from '../utils/resolveMapTemplateAvailability';
import { resolveRestrictedProQuizzes, type RestrictedProQuiz } from '../utils/resolveRestrictedQuizContent';
import { useRemoveEmptyAnswerInputs } from './useRemoveEmptyAnswerInputs';
import { useValidateQuizList } from './useValidateQuizList';
export const BuildHeaderSubmitButton = ({
  onSubmitQuiz,
  onFocusQuizField,
  onSubmittingChange
}: {
  onSubmitQuiz: () => void;
  /** 설정 탭에서 눌려도 문제 탭으로 전환한 뒤 포커스한다 (useBuildStep.focusQuizField). */
  onFocusQuizField: (fieldId: string) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}) => {
  const {
    validateAll
  } = useBuildValidate();
  const alertModal = useAlert();
  const {
    action,
    canEditQuiz,
    originQuizSet,
    mapTemplates
  } = useBuildInfoContext();
  const {
    titlePlaceholder
  } = useQuizTitle();
  const onSuccessTrack = useBuildEventTrack();
  const {
    user,
    updateUser
  } = useUser();
  const {
    userPlanManager
  } = useUserPlanManager();
  const isUpdated = action === 'UPDATE';
  const isDuplicate = action === 'DUPLICATE';
  const getSubmitAction = () => {
    if (isUpdated) return 'UPDATE';
    if (isDuplicate) return 'DUPLICATE';
    return 'CREATE';
  };
  const {
    getValues,
    reset
  } = useBuildFormContext();
  const disabledEditAlertModal = useModal(DisabledEditAlertModal);
  const client = useQueryClient();
  const router = useRouter();
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    clearPersist
  } = usePersistBuildForm();
  const validateQuizList = useValidateQuizList();
  const removeEmptyAnswerInputs = useRemoveEmptyAnswerInputs();
  const getQuizTypeSaveProps = (formData: BuildSchemaInputType) => {
    const matchQuestionCount = formData.quizList.filter(quiz => quiz.type === 'match').length;
    const orderQuestionCount = formData.quizList.filter(quiz => quiz.type === 'order').length;
    return {
      build_type: getBuildType(action),
      // 어느 진입점(CTA)으로 시작한 빌드인지. 진입 때 잡아 둔 값을 싣는다
      // (기록 없음·5분 만료면 `unknown`). 복제의 유입별 완주율이 여기서 갈린다.
      build_entry_point: getBuildEntryPoint(),
      has_match_question: matchQuestionCount > 0,
      match_question_count: matchQuestionCount,
      has_order_question: orderQuestionCount > 0,
      order_question_count: orderQuestionCount
    };
  };
  const trackSaveAttempt = (saveAttemptId: string, formData: BuildSchemaInputType) => {
    trackEvent('quiz_build_question_save_attempt', {
      save_attempt_id: saveAttemptId,
      ...getQuizTypeSaveProps(formData)
    });
  };
  const trackSaveResult = (saveAttemptId: string, formData: BuildSchemaInputType, result: 'success' | 'validation_fail' | 'api_fail', failReason?: string) => {
    trackEvent('quiz_build_question_save', {
      save_attempt_id: saveAttemptId,
      ...getQuizTypeSaveProps(formData),
      result,
      ...(failReason ? {
        fail_reason: failReason
      } : {})
    });
  };

  // 저장 이중 제출의 기계적 버그(confirm 1클릭에 mutate 2회 발사)만 막는 최소 가드.
  // 더블클릭 시 useAlert 가 NiceModal 공유 프로미스에 .then 을 2중 등록해 confirm
  // 콜백이 2번 발화할 수 있으므로, mutate 실행 직전에 재진입을 차단하고 완료 시 해제한다.
  // 버튼 disabled 나 모달 dismiss 동작은 건드리지 않아 기존 UX 와 동일하다.
  const submitInFlightRef = useRef(false);

  // 저장 요청이 실제로 진행 중인 동안(=submitInFlightRef 와 동일 수명)만 true.
  // 부모(BuildPageView)는 이 값으로 빌더 전체를 inert 처리해 편집을 막는다.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateSubmitting = (value: boolean) => {
    setIsSubmitting(value);
    onSubmittingChange?.(value);
  };
  const cleanIsDirty = () => {
    reset({
      ...getValues()
    }, {
      keepDirty: false
    });
  };
  const showSaveBlockedAlert = () => {
    focusField('templateId');
    void alertModal({
      type: 'alert',
      title: t('build:build.lockRecovery.saveBlockedTitle'),
      content: t('build:build.lockRecovery.saveBlockedDescription')
    });
  };
  const showRestrictedProQuizSaveBlockedAlert = (restrictedQuizzes: RestrictedProQuiz[]) => {
    void disabledEditAlertModal.show({
      type: 'plan',
      restrictedQuizzes,
      onEdit: firstRestrictedQuiz => {
        if (firstRestrictedQuiz) {
          onFocusQuizField(`quizList.${firstRestrictedQuiz.index}.question`);
        }
      }
    });
  };
  const onErrorAction = (error: unknown, saveAttemptId: string, formData: BuildSchemaInputType, shouldTrackResult = true) => {
    console.error('buildError', error);
    if (shouldTrackResult) {
      trackSaveResult(saveAttemptId, formData, 'api_fail', error instanceof ZepQuizApiError ? error.status : 'unknown');
    }
    if (error instanceof ZepQuizApiError) {
      if (error.status === 'CAN_NOT_USE_PREMIUM_MAP_TEMPLATE') {
        showSaveBlockedAlert();
        return;
      }
      if (error.status === 'CAN_NOT_USE_PREMIUM_QUIZ_TYPE') {
        const restrictedQuizzes = resolveRestrictedProQuizzes({
          quizList: getValues().quizList,
          // The server's rejection is authoritative even when locally cached
          // entitlement data is stale.
          hasProAccess: false
        });
        showRestrictedProQuizSaveBlockedAlert(restrictedQuizzes);
        return;
      }
    }

    // ux
    setTimeout(() => {
      const track = (btn: 'build' | 'home') => {
        logger.track('create_fail', {
          btn
        });
      };
      void (async () => {
        try {
          await alertModal({
            useOutSideClick: false,
            type: 'confirm',
            title: t('build:build.actionError.occurredBuildQuiz'),
            cancelText: t('build:build.actionError.goHome'),
            cancelAction: () => {
              track('home');
              void router.push('/public');
            },
            content: t('build:build.actionError.waitTry')
          });
          track('build');
          await router.reload();
        } catch {
          // 취소된 라우팅은 현재 오류 상태를 유지한다.
        }
      })();
    }, 1000);
  };
  const invalidateQuery = async (quizsetId?: string) => {
    const invalidateTargets = [client.invalidateQueries({
      queryKey: QUIZ_DETAIL_QUERY_KEY
    }), client.invalidateQueries({
      queryKey: MY_QUIZ_LIST_QUERY_KEY
    }), client.invalidateQueries({
      queryKey: myQuizListKey
    }),
    // 자료실은 퀴즈 전용 검색이 아니라 혼합 검색(`quiz-library`)을 쓴다.
    // 위 키만 치면 새로 만든/공개한 퀴즈가 자료실에 반영되지 않는다.
    client.invalidateQueries({
      queryKey: libraryKeys.all
    }), client.invalidateQueries({
      queryKey: getGetQuizSetBuildInfoQueryKey()
    })];
    if (quizsetId) {
      invalidateTargets.push(client.invalidateQueries({
        queryKey: getGetQuizzesStatsQueryKey({
          quizsetId
        })
      }), client.invalidateQueries({
        queryKey: getGetQuizzesDetailStatsQueryKey({
          quizsetId
        })
      }));
    }
    return Promise.all(invalidateTargets);
  };
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const {
    mutateAsync: createMutate
  } = useCreateQuizSet();
  const {
    mutateAsync: updateMutate
  } = useEditQuizSet();
  const getBuildDurationLogger = (buildStartTime: number) => {
    const logMutationElapsedTime = createElapsedTimeLogger({
      startTime: buildStartTime,
      enableLog: process.env.NODE_ENV === 'development' ? true : false,
      enableToast: false
    });
    const logBuildPageExitElapsedTime = createElapsedTimeLogger({
      startTime: buildStartTime,
      enableLog: process.env.NODE_ENV === 'development' ? true : false,
      enableToast: false
    });
    return {
      logMutationElapsedTime,
      logBuildPageExitElapsedTime
    };
  };
  const handleCreate = async (data: CreateQuizSetReq, formData: BuildSchemaInputType, saveAttemptId: string) => {
    const {
      isValid
    } = await validateQuizList();
    if (!isValid) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'quiz_list_validation');
      return;
    }

    // 이중 실행 가드: 저장이 진행 중이면 두 번째 호출은 무시한다.
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    updateSubmitting(true);
    let saveResultTracked = false;
    try {
      const buildStartTime = new Date().getTime();
      const {
        logMutationElapsedTime,
        logBuildPageExitElapsedTime
      } = getBuildDurationLogger(buildStartTime);
      const result = await createMutate({
        data
      });
      trackSaveResult(saveAttemptId, formData, 'success');
      saveResultTracked = true;
      // 복사 저장의 완료 신호. 이 뒤 곧바로 퀴즈 상세로 이동하지만 ZusToaster 가 _app 에
      // 전역 마운트라 라우팅을 넘어 살아남는다. 도착 페이지에서 쿼리를 보고 띄우면
      // 그 쿼리를 지우게 되고, 같은 값을 나중에 읽는 소비자(GC 과제 생성 추적)가 깨진다.
      if (isDuplicate) {
        zusToast.success(t('build:build.toast.duplicated'), {
          position: 'top-center'
        });
      }
      onSubmitQuiz();
      cleanIsDirty();
      const mutationCompleteDuration = logMutationElapsedTime();
      const {
        categoryTreeData
      } = result.data;
      //빌드 성공 시, 유저의 최근 퀴즈 정보 업데이트
      updateUser({
        recentQuizsetInfo: {
          createdAt: result.data.createdAt,
          schoolGrade: categoryTreeData?.schoolGrade,
          schoolSemester: categoryTreeData?.schoolSemester,
          schoolType: categoryTreeData?.schoolType
        }
      });
      await invalidateQuery(result.data.id);
      clearPersist();
      if (ltiContextManager.isInLti()) {
        await router.push(`/quiz/${result.data.id}`);
      } else {
        await router.push({
          pathname: `/quiz/${result.data.id}`,
          query: {
            [useCreateAssignmentDialogEffect.KEY]: isInGoogleClassroom,
            isDuplicate
          }
        });
      }
      onSuccessTrack(result.data.id, result.data?.countryCode || '', {
        // 만들기 시 걸린 시간 측정 - create 완료 / 퀴즈 상세 페이지 이동 시간 분리 로깅
        mutationCompleteDuration,
        buildPageExitDuration: logBuildPageExitElapsedTime()
      });
    } catch (error) {
      onErrorAction(error, saveAttemptId, formData, !saveResultTracked);
    } finally {
      submitInFlightRef.current = false;
      updateSubmitting(false);
    }
  };
  const handleUpdate = async (data: UpdateQuizSetReq, formData: BuildSchemaInputType, saveAttemptId: string) => {
    const {
      isValid
    } = await validateQuizList();
    if (!isValid) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'quiz_list_validation');
      return;
    }
    const updateStartTime = new Date().getTime();
    const {
      logMutationElapsedTime,
      logBuildPageExitElapsedTime
    } = getBuildDurationLogger(updateStartTime);
    void alertModal({
      type: 'confirm',
      title: t('update.alert.refreshMessage')
    }, () => {
      void (async () => {
        // 더블클릭 시 useAlert 가 NiceModal 공유 프로미스에 .then 을 2중 등록해
        // 이 콜백이 2번 발화할 수 있다. mutate 직전 재진입 가드로 저장은 1회만
        // 실행한다. (dismiss 로 닫으면 이 콜백은 발화하지 않아 가드가 남지 않는다.)
        if (submitInFlightRef.current) {
          return;
        }
        submitInFlightRef.current = true;
        updateSubmitting(true);
        let saveResultTracked = false;
        try {
          // 공개 폴더 안의 퀴즈를 비공개로 바꾸는 것은 여기서 확인받지 않는다. 설정 화면이 토글을 막고
          // 위치를 먼저 옮기게 한다(`TogglePublicField`).
          const result = await updateMutate({
            data
          });
          trackSaveResult(saveAttemptId, formData, 'success');
          saveResultTracked = true;
          void onSubmitQuiz();
          cleanIsDirty();
          const mutationCompleteDuration = logMutationElapsedTime();
          await invalidateQuery(result.data.id);
          if (ltiContextManager.isInLti()) {
            await router.push('/myquiz');
          } else {
            await router.push(`/quiz/${result.data.id}`);
          }
          onSuccessTrack(result.data?.id, result.data?.countryCode || '', {
            mutationCompleteDuration,
            buildPageExitDuration: logBuildPageExitElapsedTime()
          });
        } catch (error) {
          onErrorAction(error, saveAttemptId, formData, !saveResultTracked);
        } finally {
          submitInFlightRef.current = false;
          updateSubmitting(false);
        }
      })();
    });
  };
  const onSubmit = async () => {
    removeEmptyAnswerInputs();
    const formData = getValues();
    const saveAttemptId = uuidv4();
    trackSaveAttempt(saveAttemptId, formData);

    // Guest user guard - require login to save quiz
    if (!user) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'unauthenticated');
      await alertModal({
        type: 'alert',
        title: t('build:alert.guestLoginRequired.title'),
        content: t('build:alert.guestLoginRequired.description')
      }).then(() => {
        // Redirect to login with callback URL
        void router.push(`/login?callbackUrl=${encodeURIComponent(router.asPath)}`);
      });
      return;
    }
    if (!canEditQuiz) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'not_editable');
      void disabledEditAlertModal.show();
      return;
    }
    if (!validateAll()) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'build_validation');
      return;
    }
    const restrictedProQuizzes = resolveRestrictedProQuizzes({
      quizList: formData.quizList,
      hasProAccess: Object.values(QUIZ_TYPE_PLAN_FEATURE).every(feature => userPlanManager.canAccessFeature(feature))
    });
    const [firstRestrictedProQuiz] = restrictedProQuizzes;
    if (firstRestrictedProQuiz != null) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', `${firstRestrictedProQuiz.type}_plan_restricted`);
      showRestrictedProQuizSaveBlockedAlert(restrictedProQuizzes);
      return;
    }
    const mapTemplateAvailability = resolveMapTemplateAvailability({
      templateId: formData.templateId,
      visibleMapTemplates: mapTemplates,
      originMapTemplate: originQuizSet,
      hasEnoughPlanType: planType => userPlanManager.hasEnoughPlanType(planType as PlanType)
    });
    if (mapTemplateAvailability === 'unavailable-visible' || mapTemplateAvailability === 'unavailable-original' || mapTemplateAvailability === 'unknown') {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'map_template_unavailable');
      showSaveBlockedAlert();
      return;
    }
    if (!formData.title && !titlePlaceholder) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'title_required');
      // title 혹은 titlePlaceholder 중 하나는 반드시 존재해야 합니다.
      logger.debug('unexpected_title_empty', {
        title: formData.title,
        titlePlaceholder
      });
      alertDialog.open({
        header: {
          title: t('build:build.alert.title')
        }
      });
      return;
    }
    const upsertData = transformBuildFormToApiRequest({
      ...formData,
      title: formData.title || titlePlaceholder || ''
    }, {
      action: getSubmitAction(),
      quizSetId: String(router.query.id),
      originQuizSetId: isDuplicate ? originQuizSet?.originQuizSetId : undefined
    });
    if (isUpdated) {
      await handleUpdate({
        ...upsertData,
        quizSetId: router.query.id?.toString() || ''
      }, formData, saveAttemptId);
      return;
    }
    const {
      quizList: _createdQuizes,
      ...createPayload
    } = upsertData;
    await handleCreate({
      ...createPayload,
      title: String(createPayload.title),
      quizList: _createdQuizes?.map(q => ({
        ...q,
        hint: {
          ...q.hint
        }
      })) || []
    }, formData, saveAttemptId);
  };
  return <Button size="md" disabled={isSubmitting} onClick={onSubmit} leftSlot={<CheckSmallIcon size="sm" />} data-sentry-element="Button" data-sentry-component="BuildHeaderSubmitButton" data-sentry-source-file="BuildHeaderSubmitButton.tsx">
      <span className="hidden mobile-lg:inline-block">
        {isUpdated ? t('build:update.complete.button') : t('build:build.complete.button')}
      </span>
      <span className="inline-block mobile-lg:hidden">
        {t('common:global.complete.button')}
      </span>
    </Button>;
};